/**
 * /security/sessions — list and revoke active refresh tokens.
 *
 * Child of `securityRoute` — inherits its top-level mount and auth
 * gate. Renders its own minimal page chrome to match /security; both
 * pages stand outside the role-specific sidebars (admin and employee
 * both link here).
 */
import { Link, createRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionResponse } from '@kora/types';
import { securityRoute } from './security.js';
import {
  listMySessions,
  revokeAllOtherSessions,
  revokeMySession,
} from '../api/me.js';
import { ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

export const securitySessionsRoute = createRoute({
  getParentRoute: () => securityRoute,
  path: '/sessions',
  component: SessionsPage,
});

function SessionsPage(): JSX.Element {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmingRevokeAll, setConfirmingRevokeAll] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const sessionsQuery = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: listMySessions,
  });

  const revokeOne = useMutation({
    mutationFn: revokeMySession,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'sessions'] });
      setToast('Session signed out.');
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 400) {
        setToast(
          'Use Sign out to end the current session — this button only revokes others.',
        );
        return;
      }
      setToast('Could not revoke that session. Try again.');
    },
  });

  const revokeAll = useMutation({
    mutationFn: revokeAllOtherSessions,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'sessions'] });
      setConfirmingRevokeAll(false);
      setToast(
        data.revokedCount === 1
          ? '1 other device signed out.'
          : `${data.revokedCount} other devices signed out.`,
      );
    },
    onError: () => {
      setToast('Could not sign out other devices. Try again.');
    },
  });

  async function handleLogout(): Promise<void> {
    await logout();
    await navigate({ to: '/login' });
  }

  const homeTo = user?.role === 'employee' ? '/me' : '/dashboard';
  const sessions = sessionsQuery.data?.sessions ?? [];
  const otherCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <header className="border-b border-gray-900 px-6 py-3 flex items-center justify-between">
        <Link to={homeTo} className="font-serif text-xl">
          LumoHR
        </Link>
        <div className="flex items-center gap-4 text-sm">
          {user ? (
            <span className="text-gray-400" title={user.email}>
              {user.email} · {user.role}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleLogout}
            className="rounded border border-gray-700 px-3 py-1 text-xs hover:bg-gray-900"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 p-8">
        <section className="max-w-3xl mx-auto space-y-6">
          <div className="space-y-1">
            <Link
              to="/security"
              className="text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
            >
              ← Back to Security
            </Link>
            <h1 className="font-serif text-3xl">Active sessions</h1>
            <p className="text-sm text-gray-400">
              Devices currently signed in to your account. Revoking a
              session signs that device out.
            </p>
          </div>

          {toast ? (
            <div
              role="status"
              className="rounded border border-gray-800 bg-gray-950 px-4 py-2 text-sm text-gray-200"
            >
              {toast}
            </div>
          ) : null}

          {sessionsQuery.isLoading ? (
            <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
              Loading…
            </div>
          ) : sessionsQuery.isError ? (
            <div className="rounded border border-red-900 bg-red-950/40 p-6 text-sm text-red-300">
              Could not load your sessions. Try refreshing.
            </div>
          ) : sessions.length === 0 ? (
            <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
              No active sessions on file.
            </div>
          ) : (
            <div className="rounded border border-gray-900 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Session</th>
                    <th className="text-left px-4 py-2">Device</th>
                    <th className="text-left px-4 py-2">IP</th>
                    <th className="text-left px-4 py-2">Signed in</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <SessionRow
                      key={s.id}
                      session={s}
                      onRevoke={(id) => revokeOne.mutate(id)}
                      isPending={revokeOne.isPending && revokeOne.variables === s.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {otherCount > 0 ? (
            <div className="border-t border-gray-900 pt-6 space-y-3">
              <h2 className="text-sm uppercase tracking-wide text-gray-400">
                Sign out all other devices
              </h2>
              <p className="text-sm text-gray-500">
                Signs out every device except the one you&apos;re using
                right now. They&apos;ll need to sign in again.
              </p>
              {confirmingRevokeAll ? (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => revokeAll.mutate()}
                    disabled={revokeAll.isPending}
                    className="rounded bg-red-600 text-white text-sm font-medium px-4 py-2 disabled:opacity-50"
                  >
                    {revokeAll.isPending
                      ? 'Signing out…'
                      : `Sign out ${otherCount} other device${otherCount === 1 ? '' : 's'}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingRevokeAll(false)}
                    disabled={revokeAll.isPending}
                    className="rounded border border-gray-800 text-sm px-4 py-2 text-gray-300 hover:bg-gray-900"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingRevokeAll(true)}
                  className="rounded border border-gray-700 text-sm px-4 py-2 hover:bg-gray-900"
                >
                  Sign out other devices
                </button>
              )}
            </div>
          ) : null}
        </section>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row + helpers
// ---------------------------------------------------------------------------

function SessionRow({
  session,
  onRevoke,
  isPending,
}: {
  session: SessionResponse;
  onRevoke: (id: string) => void;
  isPending: boolean;
}): JSX.Element {
  return (
    <tr className="border-t border-gray-900">
      <td className="px-4 py-3">
        <span className="font-mono text-xs text-gray-300">{session.identifier}</span>
      </td>
      <td className="px-4 py-3 text-gray-300">{parseDevice(session.userAgent)}</td>
      <td className="px-4 py-3 font-mono text-xs text-gray-400">
        {session.ipAddress ?? '—'}
      </td>
      <td className="px-4 py-3 text-gray-400 text-xs">
        {formatRelative(session.issuedAt)}
      </td>
      <td className="px-4 py-3">
        {session.isCurrent ? (
          <span className="text-xs px-2 py-0.5 rounded border border-green-900 bg-green-950 text-green-400">
            Current
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded border border-gray-800 text-gray-400">
            Active
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {session.isCurrent ? (
          <button
            type="button"
            disabled
            title="Use Sign out to end the current session."
            className="text-xs text-gray-700 cursor-not-allowed"
          >
            Current
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onRevoke(session.id)}
            disabled={isPending}
            className="text-xs rounded border border-gray-700 px-3 py-1 hover:bg-gray-900 disabled:opacity-50"
          >
            {isPending ? 'Revoking…' : 'Revoke'}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Best-effort user-agent parsing. Picks one browser + one OS from a
 * short allowlist; falls back to a truncated raw UA. No UA-parser
 * library — the four labels we surface cover ~95% of real traffic
 * and we don't need precision beyond "Chrome on macOS".
 */
function parseDevice(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browsers: Array<[RegExp, string]> = [
    [/Edg\//, 'Edge'],
    [/Firefox\//, 'Firefox'],
    [/Chrome\//, 'Chrome'],
    [/Safari\//, 'Safari'],
  ];
  const oses: Array<[RegExp, string]> = [
    [/Windows NT/, 'Windows'],
    [/Mac OS X|Macintosh/, 'macOS'],
    [/Android/, 'Android'],
    [/iPhone|iPad|iOS/, 'iOS'],
    [/Linux/, 'Linux'],
  ];
  const browser = browsers.find(([re]) => re.test(ua))?.[1];
  const os = oses.find(([re]) => re.test(ua))?.[1];
  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;
  if (os) return os;
  // Truncate the raw UA so a 250-char string doesn't break the layout.
  return ua.length > 60 ? `${ua.slice(0, 57)}…` : ua;
}

/**
 * Cheap relative-time formatter. No Intl.RelativeTimeFormat — that
 * needs locale knowledge we don't have here; plain English is fine
 * for the auth chrome surface.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  // Older than a month — switch to a calendar date so distant rows
  // don't read as "120 days ago".
  return new Date(iso).toISOString().slice(0, 10);
}
